import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useLazyQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon, ArrowsRightLeftIcon, StarIcon, UserIcon } from '@heroicons/react/20/solid'
import { Input } from '../../../components/input'
import { Select } from '../../../components/select'
import { Textarea } from '../../../components/textarea'
import { Checkbox, CheckboxField, CheckboxGroup } from '../../../components/checkbox'
import { MultiSelect } from '../../../components/multi-select'
import { GET_ROUTES_FOR_REPORTS } from '../../../graphql/reports/queries'
import { GET_COUNTRY_LIST } from '../../../graphql/common/queries'
import { ErrorDialog, SuccessDialog } from '../../../components/custom/ResponseDialog'
import loadingImage from '../../../assets/images/boat-loading.gif'
import { GET_ROUTE_TYPE } from 'graphql/subscription/queries'
import Flatpickr from 'react-flatpickr'
import { GET_LOCATIONS, GET_STATION_DISTANCE_CONFIGS } from 'graphql/stationDistanceConfig/queries'
import { GET_ROUND_ROUTE_PRICE } from 'graphql/routeTrip/queries'
import { CREATE_ROUND_ROUTE_PLAN, UPDATE_ROUND_ROUTE_PLAN } from 'graphql/routeTrip/mutations'
import { SeatLayoutDropdown } from 'pages/metroRoutes/metroRoutesForm/SeatLayoutDropdown'

export default function RoundTripForm() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditMode = !!id

    const [formData, setFormData] = useState({
        price_id: '',
        seat_layout_id: '',
        end_location_id: [],
        start_location_id: [],
        country_code: [],
        end_country_code: [],
        status_code: '',
        seat_type_code: '',
        price_standard: {
            ADL: '',
            CHD: '',
            INF: '',
            SNR: '',
        },
        price_vip: {
            ADL: '',
            CHD: '',
            INF: '',
            SNR: '',
        },
        price_vip_standard: {
            ADL: '',
            CHD: '',
            INF: '',
            SNR: '',
        },
        seat_type_code_standard: 'STD',
        seat_type_code_vip: 'VIP',
        seat_type_code_vip_standard: 'S_V',
        seat_layout_id: '',
        seat_rows: '',
        seat_columns: '',
    })

    const [errors, setErrors] = useState({})
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    const [routes, setRoutes] = useState([])
    const [countries, setCountries] = useState([])
    const [locations, setLocations] = useState([])
    const [trips, setTrips] = useState([])
    const [routeTypes, setRouteTypes] = useState([])
    const [fromStationCountryFilter, setFromStationCountryFilter] = useState('')
    const [toStationCountryFilter, setToStationCountryFilter] = useState('')

    const [getCountries] = useLazyQuery(GET_COUNTRY_LIST, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.country) {
                setCountries(data.country)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch countries'))
            setShowErrDialog(true)
        },
    })

    const { data: allLocations } = useQuery(GET_LOCATIONS, {
        variables: {
            page: { limit: 100, offset: 0 },
        },
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.searchLocation?.locations) {
                setLocations(data.searchLocation.locations)
            }
        },
    })

    const [getRoundTripDetails, { loading: loadingRoundTrip }] = useLazyQuery(GET_ROUND_ROUTE_PRICE, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.getRoundRoutePrice?.round_prices?.[0]) {
                const rrt = data.getRoundRoutePrice.round_prices[0]
                setFormData({
                    seat_layout_id: rrt.seat_layout_id,
                    end_location_id: rrt.end_location_id,
                    start_location_id: rrt.start_location_id,
                    country_code: rrt.start_location.country_details.country_code || '',
                    end_country_code: rrt.end_location.country_details.country_code || '',
                    status_code: rrt.status_code || 'DRAFT',
                    seat_type_code_standard: rrt.price_details?.[0]?.seat_type_code || 'STD',
                    seat_type_code_vip: rrt.price_details?.[1]?.seat_type_code || 'VIP',
                    seat_type_code_vip_standard: rrt.price_details?.[2]?.seat_type_code || 'S_V',
                    price_standard: rrt.price_details?.[0]?.price || { ADL: 0, CHD: 0, INF: 0, SNR: 0 },
                    price_vip: rrt.price_details?.[1]?.price || { ADL: 0, CHD: 0, INF: 0, SNR: 0 },
                    price_vip_standard: rrt.price_details?.[2]?.price || { ADL: 0, CHD: 0, INF: 0, SNR: 0 },
                    seat_rows: rrt.seat_layout?.num_rows || '',
                    seat_columns: rrt.seat_layout?.num_columns || '',
                })
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch RoundTrip details'))
            setShowErrDialog(true)
        },
    })

    const [getStationDistanceConfigDetails, { loading: loadingSDC }] = useLazyQuery(GET_STATION_DISTANCE_CONFIGS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (
                data?.getStationDistanceConfigs?.station_distance_configs &&
                data.getStationDistanceConfigs.station_distance_configs.length > 0 &&
                data.getStationDistanceConfigs.station_distance_configs[0]
            ) {
                const stationDistanceConfigData = data.getStationDistanceConfigs.station_distance_configs[0]
                if (stationDistanceConfigData.to_station?.country_details?.country_code)
                    setToStationCountryFilter(stationDistanceConfigData.to_station?.country_details?.country_code)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch station distance config details'))
            setShowErrDialog(true)
        },
    })

    const statusOptions = [
        { value: 'ACT', description: 'Active' },
        { value: 'INA', description: 'Inactive' },
    ]

    const priceFields = [
        { key: 'ADL', label: 'Adult' },
        { key: 'CHD', label: 'Child' },
        { key: 'INF', label: 'Infant' },
        { key: 'SNR', label: 'Senior' },
    ]

    const [createRoundTrip, { loading: createLoading }] = useMutation(CREATE_ROUND_ROUTE_PLAN, {
        onCompleted: data => {
            if (data.addRoundRoutePrice.success) {
                setSuccessMessage(t('round routes plan created successfully'))
                setShowSucDialog(true)
                setTimeout(() => {
                    navigate('/price-config/round-trip-price')
                }, 2000)
            } else {
                setErrorMessage(data.addRoundRoutePrice.message || t('Failed to create round routes plan'))
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('An error occurred while creating influencer plan'))
            setShowErrDialog(true)
        },
    })

    const [updateRoundTrip, { loading: updateLoading }] = useMutation(UPDATE_ROUND_ROUTE_PLAN, {
        onCompleted: data => {
            if (data.updateRoundRoutePrice.success) {
                setSuccessMessage(t('round routes plan updated successfully'))
                setShowSucDialog(true)
                setTimeout(() => {
                    navigate('/price-config/round-trip-price')
                }, 2000)
            } else {
                setErrorMessage(data.updateRoundRoutePrice.message || t('Failed to update round routes plan'))
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('An error occurred while updating round routes plan'))
            setShowErrDialog(true)
        },
    })

    useEffect(() => {
        getCountries({ variables: { id: '' } })
        if (isEditMode && id) {
            getRoundTripDetails({
                variables: {
                    priceId: id,
                    page: { offset: 0, limit: 1 },
                },
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Helper function to remove __typename from objects
    const removeTypename = obj => {
        if (obj === null || obj === undefined) return obj
        if (Array.isArray(obj)) return obj.map(removeTypename)
        if (typeof obj === 'object') {
            const { __typename, ...rest } = obj
            return Object.keys(rest).reduce((acc, key) => {
                acc[key] = removeTypename(rest[key])
                return acc
            }, {})
        }
        return obj
    }
    const validateForm = () => {
        const newErrors = {}
        if (!formData.country_code) newErrors.country_code = t('Country is required')

        if (!formData.start_location_id) newErrors.start_location_id = t('Start location is required')

        if (!formData.end_location_id) newErrors.end_location_id = t('End location is required')

        if (!formData.seat_layout_id) newErrors.seat_layout_id = t('Please select a seat layout')

        if (!formData.status_code) newErrors.status_code = t('Status is required')

        if (
            formData.start_location_id &&
            formData.end_location_id &&
            formData.start_location_id === formData.end_location_id
        ) {
            newErrors.start_location_id = t('Station from must not be the same as Station to')
            newErrors.end_location_id = t('Station from must not be the same as Station to')
        }

        setErrors(newErrors)

        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = e => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }
        // Clean the data by removing __typename fields
        const cleanedData = removeTypename(formData)

        const input = {
            end_location_id: cleanedData.end_location_id,
            start_location_id: cleanedData.start_location_id,
            seat_layout_id: cleanedData.seat_layout_id,
            price_details: [
                {
                    seat_type_code: cleanedData.seat_type_code_standard,
                    price: {
                        SNR: Number(cleanedData.price_standard.SNR) || 0,
                        INF: Number(cleanedData.price_standard.INF) || 0,
                        CHD: Number(cleanedData.price_standard.CHD) || 0,
                        ADL: Number(cleanedData.price_standard.ADL) || 0,
                    },
                },
                {
                    seat_type_code: cleanedData.seat_type_code_vip,
                    price: {
                        SNR: Number(cleanedData.price_vip.SNR) || 0,
                        INF: Number(cleanedData.price_vip.INF) || 0,
                        CHD: Number(cleanedData.price_vip.CHD) || 0,
                        ADL: Number(cleanedData.price_vip.ADL) || 0,
                    },
                },
                {
                    seat_type_code: cleanedData.seat_type_code_vip_standard,
                    price: {
                        SNR: Number(cleanedData.price_vip_standard?.SNR) || 0,
                        INF: Number(cleanedData.price_vip_standard?.INF) || 0,
                        CHD: Number(cleanedData.price_vip_standard?.CHD) || 0,
                        ADL: Number(cleanedData.price_vip_standard?.ADL) || 0,
                    },
                },
            ],
            status: cleanedData.status_code === 'INA' ? 'INA' : 'ACT',
        }

        if (isEditMode) {
            updateRoundTrip({ variables: { price_id: id, round_price: input } })
        } else {
            createRoundTrip({ variables: { round_price: input } })
        }
    }

    if (loadingRoundTrip) {
        return (
            <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                <img alt="logo" src={loadingImage} className="w-[250px]" />
                <p className="pt-3 text-primary font-semibold">{t('Loading, please wait...')}</p>
            </div>
        )
    }

    const filteredStationsFrom = locations.filter(v => v.country_details?.country_code === formData.country_code)
    const filteredStationsTo = locations.filter(v => v.country_details?.country_code === formData.end_country_code)

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/price-config/round-trip-price')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back to Round Trips')}
                </button>
            </div>

            <div className="max-w-4xl mx-auto mb-8">
                <form onSubmit={handleSubmit}>
                    <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2">
                        <div className="px-4 py-6 sm:p-8">
                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
                                <div className="sm:col-span-6">
                                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                                        {isEditMode ? t('Edit Round Route Plan') : t('Create Round Route Plan')}
                                    </h2>
                                    <p className="mt-1 text-sm leading-6 text-gray-600">
                                        {isEditMode
                                            ? t('Update the Round Route plan details')
                                            : t('Add a new Round Route plan details')}
                                    </p>
                                </div>
                                {/* Stations */}
                                <div className="sm:col-span-6">
                                    <h3 className="text-lg font-medium text-gray-900 mt-4">{t('Selet Stations')}</h3>
                                </div>
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('Country From')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Select
                                        value={formData.country_code}
                                        onChange={e => {
                                            setFormData(prev => ({
                                                ...prev,
                                                country_code: e.target.value,
                                                start_location_id: '',
                                            }))
                                        }}
                                        required
                                    >
                                        <option value="" disabled>
                                            {t('Select Country')}
                                        </option>
                                        {countries.map(v => (
                                            <option value={v.alpha_iso2}>{t(v.country ?? v.alpha_iso2)}</option>
                                        ))}
                                    </Select>
                                    {errors.country_code && (
                                        <p className="text-red text-xs mt-1">{errors.country_code}</p>
                                    )}
                                </div>
                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('Station From')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Select
                                        value={formData.start_location_id}
                                        disabled={filteredStationsFrom.length <= 0}
                                        onChange={e => {
                                            const value = e.target.value
                                            // If user tries to select the same as Station To, clear Station To and show error
                                            if (value === formData.end_location_id) {
                                                setErrors(prev => ({
                                                    ...prev,
                                                    start_location_id: t(
                                                        'Station from must not be the same as Station to'
                                                    ),
                                                }))
                                                setFormData(prev => ({
                                                    ...prev,
                                                    start_location_id: value,
                                                    end_location_id: '', // reset Station To
                                                }))
                                            } else {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    start_location_id: value,
                                                }))
                                                setErrors(prev => ({
                                                    ...prev,
                                                    start_location_id: '',
                                                    end_location_id: '',
                                                }))
                                            }
                                        }}
                                        required
                                    >
                                        <option value="" disabled>
                                            {t(
                                                fromStationCountryFilter === ''
                                                    ? '<---'
                                                    : filteredStationsFrom.length <= 0
                                                      ? 'No Stations Found'
                                                      : 'Select Station'
                                            )}
                                        </option>
                                        {filteredStationsFrom.map(v => (
                                            <option
                                                key={v.id}
                                                value={v.id}
                                                disabled={v.id === formData.end_location_id}
                                            >
                                                {t(v.name.en ?? v.id)}
                                            </option>
                                        ))}
                                    </Select>
                                    {errors.start_location_id && (
                                        <p className="text-red text-xs mt-1">{errors.start_location_id}</p>
                                    )}
                                </div>
                                <div className="sm:col-span-6">
                                    <ArrowsRightLeftIcon className="w-8 h-8 text-grey-400 hover:scale-110 transition-transform"></ArrowsRightLeftIcon>
                                </div>
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('Country To')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Select
                                        value={formData.end_country_code}
                                        onChange={e => {
                                            setFormData(prev => ({
                                                ...prev,
                                                end_country_code: e.target.value,
                                                end_location_id: '',
                                            }))
                                        }}
                                        required
                                    >
                                        <option value="" disabled>
                                            {t('Select Country')}
                                        </option>
                                        {countries.map(v => (
                                            <option value={v.alpha_iso2}>{t(v.country ?? v.alpha_iso2)}</option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('Station To')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Select
                                        value={formData.end_location_id}
                                        disabled={filteredStationsTo.length <= 0}
                                        onChange={e => {
                                            const value = e.target.value
                                            if (value === formData.start_location_id) {
                                                // Immediate error
                                                setErrors(prev => ({
                                                    ...prev,
                                                    end_location_id: t(
                                                        'Station from must not be the same as Station to'
                                                    ),
                                                }))
                                                setFormData(prev => ({
                                                    ...prev,
                                                    end_location_id: '', // prevent updating to same station
                                                }))
                                            } else {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    end_location_id: value,
                                                }))
                                                setErrors(prev => ({ ...prev, end_location_id: '' }))
                                            }
                                        }}
                                        required
                                    >
                                        <option value="" disabled>
                                            {t(
                                                toStationCountryFilter === ''
                                                    ? '<---'
                                                    : filteredStationsTo.length <= 0
                                                      ? 'No Stations Found'
                                                      : 'Select Station'
                                            )}
                                        </option>
                                        {filteredStationsTo.map(v => (
                                            <option
                                                key={v.id}
                                                value={v.id}
                                                disabled={v.id === formData.start_location_id}
                                            >
                                                {t(v.name.en ?? v.id)}
                                            </option>
                                        ))}
                                    </Select>
                                    {errors.end_location_id && (
                                        <p className="text-red text-xs mt-1">{errors.end_location_id}</p>
                                    )}
                                </div>

                                {/* { Seat Layout & Status} */}
                                <div className="sm:col-span-6">
                                    <h3 className="text-lg font-medium text-gray-900">{t('Seats Category')}</h3>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Seat Layout ')}
                                        <span className="text-red">*</span>
                                    </label>
                                </div>
                                <div className="sm:col-span-full">
                                    <SeatLayoutDropdown
                                        fields={{
                                            value: formData.seat_layout_id || '',
                                            onChange: e =>
                                                setFormData(prev => ({ ...prev, seat_layout_id: e.target.value })),
                                        }}
                                        onChangeData={selected =>
                                            setFormData(prev => ({
                                                ...prev,
                                                seat_layout_id: selected.layout_id,
                                                seatRows: selected.num_rows,
                                                seatColumns: selected.num_columns,
                                            }))
                                        }
                                    />

                                    {errors.seat_layout_id && (
                                        <p className="text-error text-xs mt-1">{errors.seat_layout_id}</p>
                                    )}
                                </div>
                                <div className="sm:col-span-6">
                                    <UserIcon className="w-6 h-6 text-yellow-400 hover:scale-110 transition-transform"></UserIcon>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Seat Type (standard)')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        value={formData.seat_type_code_standard}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                seat_type_code: e.target.value,
                                            }))
                                        }
                                    ></Input>
                                </div>
                                {priceFields.map(({ key, label }) => (
                                    <div key={key} className="sm:col-span-1">
                                        <label className="block text-sm font-medium leading-6 text-gray-900">
                                            {t(label)}
                                            <span className="text-red">*</span>
                                        </label>

                                        <Input
                                            type="text"
                                            value={formData.price_standard[key]}
                                            onChange={e => {
                                                let value = e.target.value

                                                value = value.replace(/[^0-9]/g, '')

                                                if (value === '') {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        price_standard: { ...prev.price_standard, [key]: '' },
                                                    }))
                                                } else if (Number(value) >= 1) {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        price_standard: {
                                                            ...prev.price_standard,
                                                            [key]: Number(value),
                                                        },
                                                    }))
                                                }
                                            }}
                                            onBlur={() => {
                                                const value = formData.price_standard[key]
                                                if (!value || value < 1) {
                                                    setErrors(prev => ({
                                                        ...prev,
                                                        price_standard: {
                                                            ...prev.price_standard,
                                                            [key]: t('Price must be at least 1'),
                                                        },
                                                    }))
                                                }
                                            }}
                                            required
                                        />

                                        {errors?.price?.[key] && (
                                            <p className="text-red-500 text-xs mt-1">{errors.price[key]}</p>
                                        )}
                                    </div>
                                ))}
                                {/* VIP available seats */}
                                <div className="sm:col-span-6">
                                    <StarIcon className="w-6 h-6 text-blue-400 hover:scale-110 transition-transform"></StarIcon>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Seat Type (VIP) ')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        value={formData.seat_type_code_vip}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                seat_type_code: e.target.value,
                                            }))
                                        }
                                    ></Input>
                                </div>
                                {priceFields.map(({ key, label }) => (
                                    <div key={key} className="sm:col-span-1">
                                        <label className="block text-sm font-medium leading-6 text-gray-900">
                                            {t(label)}
                                            <span className="text-red">*</span>
                                        </label>

                                        <Input
                                            type="text"
                                            value={formData.price_vip[key]}
                                            onChange={e => {
                                                let value = e.target.value

                                                value = value.replace(/[^0-9]/g, '')

                                                if (value === '') {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        price_vip: { ...prev.price_vip, [key]: '' },
                                                    }))
                                                } else if (Number(value) >= 1) {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        price_vip: {
                                                            ...prev.price_vip,
                                                            [key]: Number(value),
                                                        },
                                                    }))
                                                }
                                            }}
                                            onBlur={() => {
                                                const value = formData.price_vip[key]
                                                if (!value || value < 1) {
                                                    setErrors(prev => ({
                                                        ...prev,
                                                        price_vip: {
                                                            ...prev.price,
                                                            [key]: t('Price must be at least 1'),
                                                        },
                                                    }))
                                                }
                                            }}
                                            required
                                        />

                                        {errors?.price?.[key] && (
                                            <p className="text-red-500 text-xs mt-1">{errors.price[key]}</p>
                                        )}
                                    </div>
                                ))}
                                {/* VIP & Standard available seats */}
                                <div className="sm:col-span-6 flex align-center gap-2">
                                    <UserIcon className="w-6 h-6 text-yellow-400 hover:scale-110 transition-transform"></UserIcon>

                                    <StarIcon className="w-6 h-6 text-green-400 hover:scale-110 transition-transform"></StarIcon>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Seat Type (VIP & Standard)')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        value={formData.seat_type_code_vip_standard || ''}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                seat_type_code_vip_standard: e.target.value,
                                            }))
                                        }
                                    ></Input>
                                </div>
                                {priceFields.map(({ key, label }) => (
                                    <div key={key} className="sm:col-span-1">
                                        <label className="block text-sm font-medium leading-6 text-gray-900">
                                            {t(label)}
                                            <span className="text-red">*</span>
                                        </label>

                                        <Input
                                            type="text"
                                            value={formData.price_vip_standard?.[key] || ''}
                                            onChange={e => {
                                                let value = e.target.value

                                                value = value.replace(/[^0-9]/g, '')

                                                if (value === '') {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        price_vip_standard: { ...prev.price_vip_standard, [key]: '' },
                                                    }))
                                                } else if (Number(value) >= 1) {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        price_vip_standard: {
                                                            ...prev.price_vip_standard,
                                                            [key]: Number(value),
                                                        },
                                                    }))
                                                }
                                            }}
                                            onBlur={() => {
                                                const value = formData.price_vip_standard?.[key]
                                                if (!value || value < 1) {
                                                    setErrors(prev => ({
                                                        ...prev,
                                                        price_vip_standard: {
                                                            ...prev.price_vip_standard,
                                                            [key]: t('Price must be at least 1'),
                                                        },
                                                    }))
                                                }
                                            }}
                                            required
                                        />

                                        {errors?.price_vip_standard?.[key] && (
                                            <p className="text-red-500 text-xs mt-1">
                                                {errors.price_vip_standard[key]}
                                            </p>
                                        )}
                                    </div>
                                ))}
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Status')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Select
                                        value={formData.status_code}
                                        onChange={e => setFormData({ ...formData, status_code: e.target.value })}
                                        required
                                    >
                                        <option value="">{t('Select Status')}</option>
                                        {statusOptions.map(status => (
                                            <option key={status.value} value={status.value}>
                                                {status.description}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
                            <button
                                type="button"
                                onClick={() => navigate('/price-config/round-trip-price')}
                                className="text-sm font-semibold leading-6 text-gray-900"
                            >
                                {t('Cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={createLoading || updateLoading}
                                className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50"
                            >
                                {createLoading || updateLoading
                                    ? t('Saving...')
                                    : isEditMode
                                      ? t('Update Round Route Price')
                                      : t('Create Round Route Price')}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
            {showSucDialog && (
                <SuccessDialog
                    status={showSucDialog}
                    title={t('Success')}
                    message={successMessage}
                    onClose={() => {
                        setShowSucDialog(false)
                        setSuccessMessage('')
                    }}
                />
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={showErrDialog}
                    title={t('Error')}
                    message={errorMessage}
                    onClose={() => {
                        setShowErrDialog(false)
                        setErrorMessage('')
                    }}
                />
            )}
        </>
    )
}
